import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Coins, AlertTriangle } from "lucide-react";
import { usePaymentCheckout, CheckoutStatus } from "@/lib/paymentCheckout";
import { toast } from "sonner";
import { CommercialProposal } from '@/components/academy/CommercialProposal';
import { fmtDate } from "@/lib/format";

export default function Billing() {
  const { user } = useAuth();
  const [bal, setBal] = useState(null);
  const [cfg, setCfg] = useState(null);
  const checkout = usePaymentCheckout();
  const [loadError, setLoadError] = useState("");

  const load = useCallback(async () => {
    setLoadError("");
    try {
      const [balance, config] = await Promise.all([api.get("/credits/balance"), api.get("/payments/config")]);
      setBal(balance.data); setCfg(config.data);
    } catch { setLoadError("Could not load prices and balance. Please try again."); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const buy = pack => checkout.open({
    id: pack.id, user, name: "Trifit.XPS", description: order => `${order.credits} credits · ${order.name}`,
    createOrder: async () => (await api.post("/payments/razorpay/order", { pack_id: pack.id })).data,
    verifyPayment: async response => {
      const { data } = await api.post("/payments/razorpay/verify", response);
      toast.success(data.added ? `Payment confirmed · ${data.added} credits added` : "Payment already confirmed · balance refreshed");
      await load();
    },
  });

  const packs = cfg?.packs || bal?.packs || [];

  return (
    <div>
      <header className="h-16 border-b border-border flex items-center px-8 gap-3 sticky top-0 bg-background z-10">
        <Coins className="w-5 h-5" />
        <h1 className="font-heading text-xl font-black tracking-tight">Credits &amp; Billing</h1>
      </header>

      <div className="p-8 max-w-4xl space-y-8">
        {user?.role==='admin' && <CommercialProposal/>}
        
        {loadError && <div role="alert" data-testid="billing-load-error" className="text-sm">{loadError} <button data-testid="billing-load-retry" onClick={load} className="underline ml-2">Retry</button></div>}
        {cfg && !cfg.configured && <p data-testid="billing-unavailable" className="text-sm">Checkout is temporarily unavailable. Your current credits are still available.</p>}
        <CheckoutStatus checkout={checkout} prefix="billing"/>
        {cfg?.test_mode && (
          <div className="border-l-2 border-traffic-amber pl-4 py-1" data-testid="rzp-test-note">
            <p className="text-xs text-traffic-amber font-medium">Test mode</p>
            <p className="text-xs text-muted-foreground mt-1">
              No real money moves, and the UPI QR on the checkout cannot be scanned by a real UPI app — that is
              Razorpay's sandbox, not a fault here. Use a test card (4111 1111 1111 1111, any future expiry, any CVV)
              to run a payment end to end. Swap in your live keys once your Razorpay account is activated and the
              same flow takes real money.
            </p>
          </div>
        )}
        <div className="border border-border p-6 flex items-end gap-6 flex-wrap" data-testid="credit-balance-card">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Your balance</div>
            <div className="font-heading text-6xl font-black tabular-nums mt-1" data-testid="credit-balance">
              {bal?.credits ?? "-"} <span className="text-lg text-muted-foreground">credits</span>
            </div>
          </div>
          {bal?.credits != null && bal.credits < 100 && (
            <span className="text-xs text-traffic-red border border-traffic-red px-2 py-1 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Running low · top up below</span>
          )}
        </div>

        <div>
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Top up</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {packs.map((p) => (
              <div key={p.id} className="border border-border p-5 flex flex-col" data-testid={`pack-${p.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-heading font-bold tracking-tight">{p.name}</span>
                  {p.badge && <span className="text-[10px] uppercase tracking-widest border border-border px-1.5 py-0.5 text-muted-foreground">{p.badge}</span>}
                </div>
                <div className="font-heading text-4xl font-black tabular-nums mt-3">₹{p.inr.toLocaleString("en-IN")}</div>
                <div className="text-sm text-muted-foreground mt-1">{p.credits.toLocaleString("en-IN")} credits</div>
                <button
                  data-testid={`buy-${p.id}`}
                  onClick={() => buy(p)}
                  disabled={!!checkout.busy || !cfg?.configured}
                  className="mt-4 w-full bg-foreground text-background text-sm font-medium py-2.5 hover:bg-neutral-800 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  {checkout.busy === p.id ? checkout.stage : `Pay ₹${p.inr.toLocaleString("en-IN")}` }
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="grid gap-6">
          <div className="border border-border p-5" data-testid="credit-ledger">
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-3">Recent activity</div>
            {(bal?.ledger || []).length === 0 ? <p className="text-sm text-muted-foreground">No activity yet.</p> : (
              <div className="divide-y divide-border max-h-64 overflow-y-auto">
                {bal.ledger.map((l, i) => (
                  <div key={i} className="flex items-center justify-between py-2 text-sm">
                    <span className="text-muted-foreground">{(l.reason || "").replace(/_/g, " ")} · <span className="text-[11px]">{fmtDate(l.at)}</span></span>
                    <span className={`tabular-nums font-bold ${l.delta >= 0 ? "text-traffic-green" : "text-traffic-red"}`}>{l.delta > 0 ? "+" : ""}{l.delta}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
